<template>
  <v-row>
    <v-col
      cols="12"
      md="6"
    >
      <v-card
        class="showing-amount text-center align-center"
        elevation="1"
        rounded="lg"
      >
        <div class="amount-title">
          Total Amount Due
        </div>
        <div class="amount-value">
          PKR {{ formatAmount(totalAmount) }}
        </div>
        <div class="badge">
          Session PSO-KHI-001 Complete
        </div>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      md="6"
    >
      <v-card
        class="session-card"
        elevation="1"
        rounded="lg"
      >
        <div class="session-title">
          Session Summary
        </div>

        <div class="energy-section">
          <div class="energy-row">
            <span class="energy-label">Energy Delivered</span>
            <span class="energy-value">{{ energyDelivered }} kWh</span>
          </div>
          <div class="energy-track">
            <div
              class="energy-fill"
              :style="{ width: energyPercent + '%' }"
            />
          </div>
        </div>

        <div class="meta-row">
          <div class="meta-box">
            <div class="meta-label">
              DURATION
            </div>
            <div class="meta-value">
              {{ duration }}<span class="meta-unit">m</span>
            </div>
          </div>
          <div class="meta-divider" />
          <div class="meta-box">
            <div class="meta-label">
              UNIT PRICE
            </div>
            <div class="meta-value">
              {{ unitPrice }}
            </div>
            <div class="meta-currency">
              PKR/kWh
            </div>
          </div>
        </div>

        <div class="section-divider" />

        <div class="billing-section">
          <div class="billing-row">
            <span class="billing-label">Subtotal</span>
            <span class="billing-amount">PKR {{ formatAmount(subtotal) }}</span>
          </div>
          <div class="billing-row">
            <span class="billing-label">Taxes ({{ taxRate }}%)</span>
            <span class="billing-amount">PKR {{ formatAmount(taxAmount) }}</span>
          </div>
          <div class="billing-row total-row">
            <span class="total-label">Total</span>
            <span class="total-amount">PKR {{ formatAmount(totalAmount) }}</span>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- ── Payment Method ── -->
    <v-col cols="12">
      <v-card
        class="payment-card"
        elevation="1"
        rounded="lg"
      >
        <div class="payment-title">
          Payment Method
        </div>

        <div class="payment-options">
          <div
            v-for="method in paymentMethods"
            :key="method.id"
            class="payment-option"
            :class="{ 'is-selected': selectedMethod === method.id }"
            @click="selectedMethod = method.id"
          >
            <div
              class="option-icon-wrap"
              :class="{ 'icon-selected': selectedMethod === method.id }"
            >
              <v-icon
                size="20"
                :color="selectedMethod === method.id ? '#ffffff' : '#4a5568'"
              >
                {{ method.icon }}
              </v-icon>
            </div>
            <div class="option-info">
              <div class="option-name">
                {{ method.name }}
              </div>
              <div class="option-sub">
                {{ method.sub }}
              </div>
            </div>
            <v-icon
              v-if="selectedMethod === method.id"
              size="20"
              color="#ffffff"
            >
              mdi-check-circle
            </v-icon>
          </div>
        </div>

        <!-- Pay Button -->
        <v-btn
          block
          large
          depressed
          class="pay-btn"
          :disabled="!selectedMethod"
          @click="handlePay"
        >
          <v-icon
            left
            size="16"
          >
            mdi-lock
          </v-icon>
          Pay PKR {{ formatAmount(totalAmount) }}
        </v-btn>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'SessionSummaryCard',

  props: {
    energyDelivered: { type: Number, default: 22.5 },
    energyMax:       { type: Number, default: 30 },
    duration:        { type: Number, default: 45 },
    unitPrice:       { type: Number, default: 75 },
    taxRate:         { type: Number, default: 15 },
  },

  data() {
    return {
      selectedMethod: 'pso',
      paymentMethods: [
        {
          id:   'pso',
          icon: 'mdi-card-account-details',
          name: 'PSO Fuel Card',
          sub:  'Balance: PKR 12,450.00',
        },
        {
          id:   'card',
          icon: 'mdi-credit-card-outline',
          name: 'Visa / Mastercard',
          sub:  'Ends in ●●●● 4492',
        },
        {
          id:   'wallet',
          icon: 'mdi-wallet-outline',
          name: 'Digital Wallet',
          sub:  'JazzCash or Easypaisa',
        },
      ],
    }
  },

  computed: {
    energyPercent() {
      return Math.min((this.energyDelivered / this.energyMax) * 100, 100)
    },
    subtotal() {
      return this.energyDelivered * this.unitPrice
    },
    taxAmount() {
      return this.subtotal * (this.taxRate / 100)
    },
    totalAmount() {
      return this.subtotal + this.taxAmount
    },
  },

  methods: {
    formatAmount(val) {
      return val.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    handlePay() {
      this.$emit('pay', { method: this.selectedMethod, amount: this.totalAmount })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

/* ── Shared ── */
.showing-amount,
.session-card,
.payment-card {
  font-family: 'Sora', sans-serif;
  background: #ffffff !important;
  border: 1px solid #e8ecf0 !important;
  padding: 20px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Amount Card ── */
.amount-title {
  color: gray;
  text-align: center;
}

.amount-value {
  font-size: 3rem;
  font-weight: 600;
  color: #1a202c;
}

.badge {
  color: #20a36e;
  background-color: #cff4e5;
  border-radius: 10px;
  padding: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

/* ── Session Card ── */
.session-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.01em;
}

.energy-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.energy-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.energy-label {
  font-size: 12px;
  color: #6b7280;
}

.energy-value {
  font-family: 'DM Mono', monospace;
  font-size: 13px;
  font-weight: 500;
  color: #1a202c;
}

.energy-track {
  height: 6px;
  background: #e8ecf0;
  border-radius: 99px;
  overflow: hidden;
}

.energy-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #16a34a, #22c55e);
  transition: width 0.8s ease;
}

.meta-row {
  display: flex;
  align-items: stretch;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
}

.meta-box {
  flex: 1;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-divider {
  width: 1px;
  background: #e8ecf0;
}

.meta-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #9aa5b4;
  text-transform: uppercase;
}

.meta-value {
  font-family: 'DM Mono', monospace;
  font-size: 26px;
  font-weight: 500;
  color: #1a202c;
  line-height: 1.1;
}

.meta-unit {
  font-size: 14px;
  color: #6b7280;
}

.meta-currency {
  font-size: 10px;
  font-weight: 600;
  color: #9aa5b4;
  letter-spacing: 0.06em;
}

.section-divider {
  height: 1px;
  background: #f0f2f5;
  margin: 0 -20px;
}

.billing-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.billing-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.billing-label {
  font-size: 12px;
  color: #6b7280;
}

.billing-amount {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: #4a5568;
}

.total-row {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e8ecf0;
}

.total-label {
  font-size: 13px;
  font-weight: 700;
  color: #1a202c;
}

.total-amount {
  font-family: 'DM Mono', monospace;
  font-size: 14px;
  font-weight: 500;
  color: #20a36e;
}

/* ══════════════════════════
   PAYMENT METHOD
══════════════════════════ */
.payment-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.01em;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1.5px solid #e8ecf0;
  border-radius: 12px;
  cursor: pointer;
  background: #f9fafb;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.payment-option:hover {
  border-color: #a0c4b8;
  background: #f0f7f4;
}

.payment-option.is-selected {
  border-color: #0d4a38;
  background: #0d4a38;
  box-shadow: 0 4px 16px rgba(13, 74, 56, 0.22);
}

.option-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e8ecf0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
}

.option-icon-wrap.icon-selected {
  background: rgba(255, 255, 255, 0.15);
}

.option-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a202c;
  transition: color 0.2s;
}

.is-selected .option-name {
  color: #ffffff;
}

.option-sub {
  font-size: 11px;
  color: #9aa5b4;
  transition: color 0.2s;
}

.is-selected .option-sub {
  color: rgba(255, 255, 255, 0.60);
}

/* ── Pay Button ── */
.pay-btn {
  font-family: 'Sora', sans-serif !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  background: #0d4a38 !important;
  color: #ffffff !important;
  border-radius: 12px !important;
  height: 52px !important;
  margin-top: 4px;
  box-shadow: 0 4px 16px rgba(13, 74, 56, 0.28) !important;
  transition: background 0.2s, box-shadow 0.2s !important;
  letter-spacing: 0.01em !important;
}

.pay-btn:hover:not(.v-btn--disabled) {
  background: #0f5a45 !important;
  box-shadow: 0 6px 20px rgba(13, 74, 56, 0.38) !important;
}

.pay-btn.v-btn--disabled {
  background: #d1d5db !important;
  color: #9aa5b4 !important;
  box-shadow: none !important;
}
</style>